import {useState, useCallback, useEffect, useMemo, useContext} from "react";
import {useGetMyRecentActivity, type RecentActivityItemDto} from "@/app2/shared/data";
import {LocaleContext, resolveLocale} from "@/app2/shared/contexts";

/**
 * Recent activity with "load more": React Query only holds the current page, so we merge
 * each fetched page into local state to show the full list accumulated so far.
 */
export const useRecentActivity = () => {
	const [{locale}] = useContext(LocaleContext);
	const resolvedLocale = resolveLocale(locale);
	const [page, setPage] = useState(1);
	const pageSize = 10;
	const [activitiesByPage, setActivitiesByPage] = useState<
		Record<number, RecentActivityItemDto[]>
	>({});

	const {data, isLoading, isError, error} = useGetMyRecentActivity({
		page,
		pageSize,
		locale: resolvedLocale,
	});

	useEffect(() => {
		setPage(1);
		setActivitiesByPage({});
	}, [resolvedLocale]);

	useEffect(() => {
		if (data?.page === undefined || !data.data) return;
		setActivitiesByPage(prev => ({...prev, [data.page]: data.data}));
	}, [data]);

	const activities = useMemo(() => {
		const pages = Object.keys(activitiesByPage)
			.map(Number)
			.sort((a, b) => a - b);
		return pages.flatMap(p => activitiesByPage[p] ?? []);
	}, [activitiesByPage]);

	const hasMore = data ? data.totalCount > page * pageSize : false;

	const loadMore = useCallback(() => {
		if (!isLoading && hasMore) {
			setPage(prev => prev + 1);
		}
	}, [isLoading, hasMore]);

	return {
		activities,
		totalCount: data?.totalCount ?? 0,
		isLoading,
		isError,
		error,
		hasMore,
		loadMore,
		page
	};
};
